import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { HttpAuthenticationType, httpAuthType } from 'app/model/pipeline/web.activity';
import {
    WebActivitySPNAuthenticationType,
    WebHookActivity,
    WebhookActivitySettingsType
} from 'app/model/pipeline/webhook.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import _ from 'lodash';
import { AdapterContext, convertParametersToModel, Credential, GlobalVariables, LinkedService, ModelConstants } from 'shell-models-lib';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from '../linkedservices/azure-key-vault.util';
import { isExpressionObject } from 'app/shared/utils/expression.util';

import { IntegrationRuntime } from '../../integrationRuntime/integrationRuntime';
import { WebActivityLinkedService } from 'app/model/linkedservice/web-activity.linkedservice';
import { ActivityConnectionContext, IWebHookActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { ModelService } from 'app/model/services/model.service';
import { IFrameService } from 'app/services/iframe.service';
import { A365WorkspaceCacheService } from 'app/services/metadata-client/A365workspace-cache.service';
import { RestServiceLinkedService } from 'app/model/linkedservice/rest-service.linkedservice';
import { isUrlValid, getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(WebHookActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: __IS_FABRIC_DPE__ ? '*' : undefined,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            connectVia: '*',
            method: '*',
            url: '*',
            relativeUrl: __IS_FABRIC_DPE__ ? '*' : undefined,
            resource: __IS_FABRIC_DPE__ ? '*' : undefined,
            headers: '*',
            body: '*',
            timeout: '*',
            reportStatusOnCallBack: '*',
            authentication: (context: AdapterContext, value: Model.WebActivityAuthentication) => {
                if (
                    value &&
                    value.type &&
                    ['NONE', 'BASIC', 'MSI', 'CLIENTCERTIFICATE', 'SERVICEPRINCIPAL'].indexOf(
                        value.type.toUpperCase()
                    ) >= 0
                ) {
                    return '*';
                }
                // Webhook activity UI only support None, Basic, ClientCertificate, ServicePrincipal auth.
                // Returning anything other than * or ? will add it to raw JSON.
                return undefined;
            },
            userTenant: '*'
        },
        externalReferences: '*'
    }
})
export class WebHookActivityAdapter extends ActivityAsyncAdapter<
    WebHookActivity,
    Model.WebHookActivity,
    IWebHookActivityContext
> {
    public isTrident: boolean = __IS_FABRIC_DPE__;
    public model(): IModel {
        return Metamodel.modelFor(WebHookActivity);
    }
    public override async toFabricCopilotContext(activity: WebHookActivity): Promise<IWebHookActivityContext> {
        if (activity) {
            const activityContext: IWebHookActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.restLinkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    LinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.restLinkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.restLinkedService);
                activityContext.RelativeUrl = ValueAdapter.toResource(activity, WebHookActivity.RELATIVEURL, true);
                activityContext.Method = activity.method;
                activityContext.Body = JSON.stringify(ValueAdapter.toResource(activity, WebHookActivity.BODY, true));
                activityContext.Headers = toParameterObject(activity.headers, false, undefined, true);
                activityContext.Timeout = ValueAdapter.toResource(activity, WebHookActivity.WEBHOOK_TIMEOUT, true);
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IWebHookActivityContext,
        model?: WebHookActivity
    ): Promise<WebHookActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.restLinkedService = (
                    await findLinkedServiceFromContext<WebActivityLinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.restLinkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                              WebActivityLinkedService,
                              Model.LinkedServiceResource,
                              ActivityConnectionContext
                          >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    await lsAdapter?.fromFabricCopilotContext(context.Connection, model.restLinkedService);
                    ValueAdapter.toModel(model, WebHookActivity.RELATIVEURL, context.RelativeUrl);
                    try {
                        ValueAdapter.toModel(model, WebHookActivity.BODY, JSON.parse(context.Body));
                    } catch (e) {}
                    ValueAdapter.toModel(model, WebHookActivity.WEBHOOK_TIMEOUT, context.Timeout);
                    if (context.Method) {
                        model.method = context.Method as 'POST';
                    }
                    if (context.Headers) {
                        model.headers = toParameterClass(context.Headers, model);
                    }
                }
            }
        }
        return model;
    }

    public async postToModel(activity: WebHookActivity): Promise<void> {
        await super.postToModel(activity);
        activity.showDEPWarning = false;
        if (__IS_A365__ && activity.connectVia == null && !GlobalVariables.isCmdApiApp) {
            const a365WorkspaceCacheService = await getServiceInstance<A365WorkspaceCacheService>(
                this.context.contextData,
                'A365WorkspaceCacheService'
            );
            activity.showDEPWarning = a365WorkspaceCacheService.isDEPEnabled;
        }
    }

    protected async _toResource(activity: WebHookActivity): Promise<Model.WebHookActivity> {
        const modelActivity: Model.WebHookActivity = super._getCommonActivityProperties(
            activity,
            Metamodel.modelFor(WebHookActivity).name
        ) as Model.WebHookActivity;

        const headers = toParameterObject(activity.headers);
        modelActivity.typeProperties = {
            url: ValueAdapter.toResource(activity, WebHookActivity.URL),
            relativeUrl: ValueAdapter.toResource(activity, WebHookActivity.RELATIVEURL),
            connectVia: activity.connectVia ? this._toIntegrationRuntimeModel(activity.connectVia) : undefined,
            method: activity.method || undefined,
            headers: headers ? headers : {},
            body: ValueAdapter.toResource(activity, WebHookActivity.BODY),
            timeout: activity.webhookTimeout || undefined,
            reportStatusOnCallBack: ValueAdapter.toResource(activity, WebHookActivity.REPORTSTATUSONCALLBACK),
            disableCertValidation: ValueAdapter.toResource(activity, WebHookActivity.DISABLECERTVALIDATION),
            resource: ValueAdapter.toResource(activity, WebHookActivity.RESOURCE)
        };

        let linkedService: LinkedService = activity.restLinkedService as WebActivityLinkedService;
        if (this.context.adapterConfig?.convertToFabric) {
            const linkedServiceName = ValueAdapter.isTemplated(activity, WebHookActivity.URL)
                ? `Url: ${ValueAdapter.toResource(activity, WebHookActivity.URL, true)}`
                : activity.url && isUrlValid(activity.url)
                    ? `Url: ${new URL(activity.url).hostname}`
                    : 'Url: default';

            linkedService = new RestServiceLinkedService(linkedServiceName);
        }

        if (linkedService) {
            modelActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                linkedService,
                activity.linkedServiceParameters
            );
        }

        // If activity has authentication type, populate the respective fields for that auth type
        if (!this.context.adapterConfig?.convertToFabric && activity.authentication && activity.authentication.type !== 'None') {
            // irrespective of auth type, if auth ref is credential, populate cred and resource instead of type
            if (
                activity.authentication.refType === 'Credential' ||
                activity.authentication.type === 'UserAssignedManagedIdentity'
            ) {
                modelActivity.typeProperties.authentication = {};
                modelActivity.typeProperties.authentication.resource = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.RESOURCE
                );
                modelActivity.typeProperties.authentication.credential = ReferenceAdapter.toResource(
                    activity.authentication.credential,
                    this.context
                ) as Model.CredentialReference;
            } else {
                modelActivity.typeProperties.authentication = {
                    type: activity.authentication.type
                };
            }

            // inline options. For credential, the authentication.type will be null and following checks will be skipped.
            if (activity.authentication.type === 'Basic') {
                modelActivity.typeProperties.authentication.username = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.USERNAME
                );
            } else if (activity.authentication.type === 'ClientCertificate' && activity.authentication.pfx) {
                modelActivity.typeProperties.authentication.pfx = passwordOrAKVtoSecretBase(
                    activity.authentication.pfx,
                    this.context
                );
            } else if (activity.authentication.type === 'MSI') {
                modelActivity.typeProperties.authentication.resource = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.RESOURCE
                );
            } else if (activity.authentication.type === 'ServicePrincipal') {
                if (
                    activity.authentication.pfx &&
                    activity.authentication.spnAuthenticationFormat ===
                        WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_CERTIFICATE
                ) {
                    modelActivity.typeProperties.authentication.pfx = passwordOrAKVtoSecretBase(
                        activity.authentication.pfx,
                        this.context
                    );
                } else if (
                    activity.authentication.password &&
                    activity.authentication.spnAuthenticationFormat ===
                        WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET
                ) {
                    modelActivity.typeProperties.authentication.password = passwordOrAKVtoSecretBase(
                        activity.authentication.password,
                        this.context
                    );
                }

                modelActivity.typeProperties.authentication.userTenant = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.USERTENANT
                );
                modelActivity.typeProperties.authentication.username = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.USERNAME
                );
                modelActivity.typeProperties.authentication.resource = ValueAdapter.toResource(
                    activity.authentication,
                    HttpAuthenticationType.RESOURCE
                );
            }

            // Only add password in the model if the field exists in the authentication type
            const hasPasswordField: boolean =
                activity.authentication.type === 'Basic' ||
                activity.authentication.type === 'ClientCertificate' ||
                (activity.authentication.type === 'ServicePrincipal' &&
                    activity.authentication.spnAuthenticationFormat ===
                        WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET);

            if (activity.authentication.password && hasPasswordField) {
                modelActivity.typeProperties.authentication.password = passwordOrAKVtoSecretBase(
                    activity.authentication.password,
                    this.context
                );
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on http activity
            _.merge(modelActivity, parsed);
        }

        return modelActivity;
    }

    protected async _toModel(modelActivity: Model.WebHookActivity): Promise<WebHookActivity> {
        const activity: WebHookActivity = WebHookActivity.MODEL.create<WebHookActivity>(
            modelActivity.name,
            modelActivity.description
        );

        this._updatedCommonProperties(modelActivity, activity);

        if (modelActivity.typeProperties) {
            if (modelActivity.typeProperties.url) {
                activity.activitySettings = WebhookActivitySettingsType.INLINE;
                ValueAdapter.toModel(activity, WebHookActivity.URL, modelActivity.typeProperties.url);
            }

            if (modelActivity.typeProperties.relativeUrl) {
                if (__IS_FABRIC_DPE__) {
                    activity.activitySettings = WebhookActivitySettingsType.CONNECTION;
                }
                ValueAdapter.toModel(activity, WebHookActivity.RELATIVEURL, modelActivity.typeProperties.relativeUrl);
            }
            if (modelActivity.typeProperties.resource) {
                ValueAdapter.toModel(activity, WebHookActivity.RESOURCE, modelActivity.typeProperties.resource);
            }
            ValueAdapter.toModel(activity, WebHookActivity.BODY, modelActivity.typeProperties.body);
            activity.webhookTimeout = modelActivity.typeProperties.timeout || undefined;
            ValueAdapter.toModel(
                activity,
                WebHookActivity.REPORTSTATUSONCALLBACK,
                modelActivity.typeProperties.reportStatusOnCallBack
            );

            activity.method = modelActivity.typeProperties.method
                ? (modelActivity.typeProperties.method.toUpperCase() as 'POST')
                : undefined;
            activity.headers = toParameterClass(modelActivity.typeProperties.headers, activity);

            ValueAdapter.toModel(
                activity,
                WebHookActivity.DISABLECERTVALIDATION,
                modelActivity.typeProperties.disableCertValidation
            );

            if (modelActivity.typeProperties.connectVia) {
                activity.connectVia = ReferenceAdapter.toProxyModel<IntegrationRuntime>(
                    Metamodel.modelFor(IntegrationRuntime),
                    modelActivity.typeProperties.connectVia,
                    this.context
                );
            }

            if (modelActivity.typeProperties.authentication) {
                // Check if disableCertValidation is used inside client certificate authentication, move it in typeproperties
                if (
                    !activity.disableCertValidation &&
                    modelActivity.typeProperties.authentication.disableCertValidation
                ) {
                    activity.disableCertValidation = modelActivity.typeProperties.authentication.disableCertValidation;
                }

                if (modelActivity.typeProperties.authentication.credential) {
                    ValueAdapter.toModel(
                        activity.authentication,
                        HttpAuthenticationType.RESOURCE,
                        modelActivity.typeProperties.authentication.resource
                    );
                    activity.authentication.credential = ReferenceAdapter.toProxyModel(
                        Metamodel.modelFor(Credential),
                        modelActivity.typeProperties.authentication.credential,
                        this.context
                    );
                    activity.authentication.type = modelActivity.typeProperties.authentication
                        .authenticationMethod as httpAuthType;
                }

                switch (modelActivity.typeProperties.authentication.type) {
                    case 'Basic':
                        activity.authentication.type = 'Basic';
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.USERNAME,
                            modelActivity.typeProperties.authentication.username
                        );
                        activity.authentication.password = isExpressionObject(
                            modelActivity.typeProperties.authentication.password
                        )
                            ? modelActivity.typeProperties.authentication.password.value
                            : secretBaseToPasswordOrAKV(
                                  modelActivity.typeProperties.authentication.password,
                                  true,
                                  this.context
                              );
                        break;
                    case 'ClientCertificate':
                        activity.authentication.type = 'ClientCertificate';
                        activity.authentication.pfx = isExpressionObject(
                            modelActivity.typeProperties.authentication.pfx
                        )
                            ? modelActivity.typeProperties.authentication.pfx.value
                            : secretBaseToPasswordOrAKV(
                                  modelActivity.typeProperties.authentication.pfx,
                                  true,
                                  this.context
                              );
                        activity.authentication.password = isExpressionObject(
                            modelActivity.typeProperties.authentication.password
                        )
                            ? modelActivity.typeProperties.authentication.password.value
                            : secretBaseToPasswordOrAKV(
                                  modelActivity.typeProperties.authentication.password,
                                  true,
                                  this.context
                              );
                        break;
                    case 'MSI':
                        activity.authentication.type = 'MSI';
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.RESOURCE,
                            modelActivity.typeProperties.authentication.resource
                        );
                        break;
                    case 'ServicePrincipal':
                        activity.authentication.type = 'ServicePrincipal';

                        // Take expressions or string value for each one
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.USERTENANT,
                            modelActivity.typeProperties.authentication.userTenant
                        );
                        // For backward compatibility, if userTenant defined outside authentication, move it in
                        if (!activity.authentication.userTenant && modelActivity.typeProperties.userTenant) {
                            activity.authentication.userTenant = modelActivity.typeProperties.userTenant;
                        }

                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.USERNAME,
                            modelActivity.typeProperties.authentication.username
                        );
                        ValueAdapter.toModel(
                            activity.authentication,
                            HttpAuthenticationType.RESOURCE,
                            modelActivity.typeProperties.authentication.resource
                        );

                        if (modelActivity.typeProperties.authentication.pfx) {
                            activity.authentication.spnAuthenticationFormat = 'Pfx';
                            activity.authentication.pfx = isExpressionObject(
                                modelActivity.typeProperties.authentication.pfx
                            )
                                ? modelActivity.typeProperties.authentication.pfx.value
                                : secretBaseToPasswordOrAKV(
                                      modelActivity.typeProperties.authentication.pfx,
                                      true,
                                      this.context
                                  );
                        } else {
                            activity.authentication.spnAuthenticationFormat = 'Password';
                            activity.authentication.password = isExpressionObject(
                                modelActivity.typeProperties.authentication.password
                            )
                                ? modelActivity.typeProperties.authentication.password.value
                                : secretBaseToPasswordOrAKV(
                                      modelActivity.typeProperties.authentication.password,
                                      true,
                                      this.context
                                  );
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        if (modelActivity.linkedServiceName != null && __IS_FABRIC_DPE__) {
            const ls = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.webActivityLinkedService,
                activity
            );
            activity.restLinkedService = ls ? (ls as WebActivityLinkedService) : ReferenceAdapter.toProxyModel<WebActivityLinkedService>(
                Metamodel.modelFor(WebActivityLinkedService),
                modelActivity.linkedServiceName,
                this.context
            );

            if (modelActivity.linkedServiceName.parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.restLinkedService,
                    activity.linkedServiceParameters,
                    modelActivity.linkedServiceName.parameters
                );
            }
            activity.activitySettings = WebhookActivitySettingsType.CONNECTION;
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, WebHookActivity.MODEL));

        return activity;
    }

    protected _toIntegrationRuntimeModel(connectVia: IntegrationRuntime): Model.IntegrationRuntimeReference {
        return ReferenceAdapter.toResource(connectVia, this.context) as Model.IntegrationRuntimeReference;
    }
}
